import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Check, ZoomIn, ZoomOut, Move } from "lucide-react";

import { getApiUrl } from '@/config/api';

const API = getApiUrl();

export default function VideoMouthPosition({ open, onClose, sceneId, onSave }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [frameUrl, setFrameUrl] = useState(null);
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });
  const [mouthPosition, setMouthPosition] = useState({ x: 0.5, y: 0.3, width: 0.05, height: 0.03 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  // Load first frame when dialog opens
  useEffect(() => {
    if (open && sceneId) {
      loadVideoFrame();
    }
  }, [open, sceneId]);

  const loadVideoFrame = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/scenes/${sceneId}/video-frame`);
      if (response.data.success) {
        setFrameUrl(response.data.frame_url);
        setFrameSize({ width: response.data.width, height: response.data.height });
      }
    } catch (error) {
      console.error('Error loading video frame:', error);
      toast.error('Failed to load video frame');
    } finally {
      setLoading(false);
    }
  };

  // Handle mouse down on mouth box
  const handleMouthMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  // Handle mouse down on container (for panning)
  const handleContainerMouseDown = (e) => {
    if (e.target.closest('[data-mouth-box]')) return;
    setIsPanning(true);
    setPanStart({ x: e.clientX, y: e.clientY });
  };

  // Handle mouse move
  const handleMouseMove = (e) => {
    if (!containerRef.current) return;

    if (isPanning) {
      const dx = (e.clientX - panStart.x) / containerRef.current.offsetWidth;
      const dy = (e.clientY - panStart.y) / containerRef.current.offsetHeight;
      setPanOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setPanStart({ x: e.clientX, y: e.clientY });
      return;
    }

    if (isDragging) {
      const rect = containerRef.current.getBoundingClientRect();
      const dx = (e.clientX - dragStart.x) / (rect.width * zoom);
      const dy = (e.clientY - dragStart.y) / (rect.height * zoom);

      setMouthPosition(prev => ({
        ...prev,
        x: Math.max(0.02, Math.min(0.98, prev.x + dx)),
        y: Math.max(0.02, Math.min(0.98, prev.y + dy))
      }));

      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setIsDragging(false);
    setIsPanning(false);
  };

  // Handle wheel for zoom
  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.2 : 0.2;
      setZoom(prev => Math.max(0.5, Math.min(5, prev + delta)));
    }
  };

  // Adjust mouth size
  const adjustSize = (dimension, delta) => {
    setMouthPosition(prev => ({
      ...prev,
      [dimension]: Math.max(0.01, Math.min(0.3, prev[dimension] + delta))
    }));
  };

  // Save position
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/scenes/${sceneId}/mouth-position`, {
        mouth_position: mouthPosition
      });
      toast.success('Mouth position saved!');
      onSave?.(mouthPosition);
      onClose();
    } catch (error) {
      console.error('Error saving mouth position:', error);
      toast.error('Failed to save mouth position');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-xl text-white">Set Mouth Position on Video</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Position the green box exactly where the mouth sprites should appear in this video.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            <span className="ml-3 text-zinc-400">Loading video frame...</span>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Controls */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              {/* Zoom controls */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setZoom(z => Math.max(0.5, z - 0.2))}>
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-sm text-zinc-300 w-16 text-center">{Math.round(zoom * 100)}%</span>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setZoom(z => Math.min(5, z + 0.2))}>
                  <ZoomIn className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => { setZoom(1); setPanOffset({ x: 0, y: 0 }); }}
                  className="text-xs text-zinc-400"
                >
                  Reset View
                </Button>
              </div>

              {/* Size controls */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <span className="text-zinc-400">W:</span>
                  <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => adjustSize('width', -0.005)}>-</Button>
                  <span className="text-white w-12 text-center">{Math.round(mouthPosition.width * 100)}%</span>
                  <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => adjustSize('width', 0.005)}>+</Button>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-zinc-400">H:</span>
                  <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => adjustSize('height', -0.005)}>-</Button>
                  <span className="text-white w-12 text-center">{Math.round(mouthPosition.height * 100)}%</span>
                  <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => adjustSize('height', 0.005)}>+</Button>
                </div>
              </div>
            </div>

            <p className="text-xs text-zinc-500 flex items-center gap-2">
              <Move className="w-3 h-3" />
              Drag image to pan • Drag green box to position • Ctrl+scroll to zoom
            </p>

            {/* Frame with mouth box */}
            <div 
              ref={containerRef}
              className="relative bg-zinc-800 rounded-lg overflow-hidden select-none cursor-grab"
              style={{ height: '450px' }}
              onMouseDown={handleContainerMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
            >
              {frameUrl && (
                <div 
                  className="absolute"
                  style={{
                    width: `${zoom * 100}%`,
                    height: `${zoom * 100}%`,
                    left: `${50 + panOffset.x * 100}%`,
                    top: `${50 + panOffset.y * 100}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <img 
                    src={frameUrl}
                    alt="Video frame"
                    className="w-full h-full object-contain"
                    draggable={false}
                  />

                  {/* Mouth position box */}
                  <div
                    data-mouth-box="true"
                    className={`absolute border-2 border-green-500 bg-green-500/30 cursor-move rounded ${isDragging ? 'border-green-400 bg-green-500/40' : ''}`}
                    style={{
                      left: `${mouthPosition.x * 100 - mouthPosition.width * 50}%`,
                      top: `${mouthPosition.y * 100 - mouthPosition.height * 50}%`,
                      width: `${mouthPosition.width * 100}%`,
                      height: `${mouthPosition.height * 100}%`,
                    }}
                    onMouseDown={handleMouthMouseDown}
                  >
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-green-600 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap font-medium">
                      Mouth Position
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Position info */}
            <div className="text-xs text-zinc-500 flex gap-4">
              <span>X: {Math.round(mouthPosition.x * 100)}%</span>
              <span>Y: {Math.round(mouthPosition.y * 100)}%</span>
              <span>Size: {Math.round(mouthPosition.width * 100)}% × {Math.round(mouthPosition.height * 100)}%</span>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSave}
            disabled={loading || saving}
            className="bg-green-600 hover:bg-green-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Save Position
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
